import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { BaseLivlyApiResponse } from "../types/Base";
import { BASE_API_URL } from "../utils/constants";
import { identifyUser } from "../utils/analytics";
import { OnboardingStatus, User } from "../types/User";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import rg4js from "raygun4js";
import { setSentryUser } from "@/utils/sentry";
import { Spinner } from "@/components/Spinner";
import { ErrorBoundary } from "react-error-boundary";
import Oops from "@/components/Oops";
import { IdleTimeoutProvider } from "./IdleTimeoutProvider";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useAppVersion } from "@/hooks/useAppVersion";
import { useGetOnboardingStatuses } from "./ChecklistProvider";
import { encodeId } from "@/utils/idObfuscation";
import { useDecodedParam } from "@/hooks/useEncodedIds";
import Alert from "@/components/Alert";

rg4js("apiKey", import.meta.env.VITE_RAYGUN_KEY);
rg4js("enableCrashReporting", true);
rg4js("logContentsOfXhrCalls", true);
rg4js("withTags", [import.meta.env.VITE_ENVIRONMENT!]);

rg4js("options", {
  ignore3rdPartyErrors: true
});

// Discard errors caused by Firefox iOS and Brave/MetaMask browser-extension injections
// (__firefox__, ethereum). These are not app bugs — just extension noise (~35% of daily errors).
rg4js("onBeforeSend", (payload) => {
  const msg = payload?.Details?.Error?.Message ?? "";

  const extensionPatterns = [
    /window\.__firefox__/i,
    /window\.ethereum/i,
    /__firefox__\.metadata/i,
    /__firefox__\.includeOnce/i,
    /can't find variable: __firefox__/i,
    /Cannot read properties of undefined \(reading 'ethereum'\)/i
  ];

  if (extensionPatterns.some((pattern) => pattern.test(msg))) {
    return false; // discard — not a real app error
  }

  return payload;
});

const getUserMe = async (leaseId: string) => {
  const result = await axios.get<BaseLivlyApiResponse<User>>(
    `${BASE_API_URL}/livly/users/me?v=202404&leaseId=${leaseId}`
  );

  return result.data.Data;
};

interface UserContextShape {
  user: User;
  isChatUserSet: boolean;
  isLoading: boolean;
  refreshUser: () => void;
  setIsChatUserSet: (isSet: boolean) => void;
  updateUser: (user: Partial<User>) => void;
  onboardingData: OnboardingStatus[];
}

const initialState: UserContextShape = {
  user: {} as User,
  isLoading: true,
  isChatUserSet: false,
  refreshUser: () => {},
  setIsChatUserSet: () => {},
  updateUser: () => {},
  onboardingData: [] as OnboardingStatus[]
};

const UserContext = createContext<UserContextShape>(
  initialState as UserContextShape
);

function UserProvider() {
  const ldClient = useLDClient();
  const appVersion = useAppVersion();
  const leaseId: number | null = useDecodedParam("leaseId");
  const navigate = useNavigate();
  const location = useLocation();
  const [isChatUserSet, setIsChatUserSet] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  // Redirect legacy numeric IDs to encoded IDs
  useEffect(() => {
    const segments = location.pathname.split("/");
    let hasNumericId = false;

    const encodedSegments = segments.map((segment) => {
      const asNumber = parseInt(segment, 10);
      if (!isNaN(asNumber) && asNumber >= 0 && String(asNumber) === segment) {
        hasNumericId = true;
        return encodeId(asNumber);
      }
      return segment;
    });

    if (hasNumericId) {
      const newPath =
        encodedSegments.join("/") + location.search + location.hash;
      navigate(newPath, { replace: true, state: location.state });
    }
  }, [location.pathname, location.search, location.hash, location.state, navigate]);

  const updateUser = (newUser: Partial<User>) => {
    if (!user) {
      return;
    }
    setUser({ ...user, ...newUser });
  };

  const { isLoading, refetch } = useQuery({
    queryKey: ["user", leaseId],
    queryFn: () => getUserMe(String(leaseId!)),
    onError: () => {
      navigate("/no-access");
    },
    onSuccess: (data) => {
      setUser(data);

      if (data.leases.length === 0) {
        return navigate("/no-access");
      }

      identifyUser(data);
      rg4js("setUser", {
        identifier: data.email,
        isAnonymous: false,
        email: data.email,
        firstName: data.firstName,
        fullName: data.fullName
      });
      setSentryUser(data);
    }
  });

  //call this api once user is loaded
  const { data = [], isLoading: onboardingStatusLoading } =
    useGetOnboardingStatuses(
      Number(user?.propertyUnitLeaseId),
      Number(user?.userId),
      !!user?.propertyUnitLeaseId && !!user?.userId
    );

  useEffect(() => {
    if (location?.pathname) {
      rg4js("trackEvent", {
        type: "pageView",
        path: "/" + window.location.pathname
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (user && ldClient) {
      ldClient.identify({
        kind: "user",
        key: `Web_Resident ${import.meta.env.VITE_ENVIRONMENT}_${appVersion}`,
        device: navigator?.userAgent,
        appName: "Resident",
        platform: "Web",
        appVersion
      });
    }
  }, [user, ldClient]);

  const refreshUser = () => {
    refetch();
  };

  const value = useMemo(
    () => ({
      user: user!,
      isLoading: isLoading || onboardingStatusLoading,
      refreshUser,
      isChatUserSet,
      setIsChatUserSet,
      updateUser,
      onboardingData: data
    }),
    [
      user,
      isLoading,
      refreshUser,
      isChatUserSet,
      setIsChatUserSet,
      updateUser,
      data
    ]
  );

  if (user && leaseId === null) {
    return (
      <Navigate
        to="/not-found"
        replace={true}
        state={{ user, from: location.pathname }}
      />
    );
  }

  return (
    <ErrorBoundary fallback={<Oops />}>
      <UserContext.Provider value={value}>
        <IdleTimeoutProvider>
          {isLoading ? (
            <div className="h-screen flex justify-center items-center">
              <Spinner color="livly" size="xl" />
            </div>
          ) : user != null ? (
            <Outlet />
          ) : null}
        </IdleTimeoutProvider>
      </UserContext.Provider>
    </ErrorBoundary>
  );
}

export { UserProvider };

export default function useLivlyUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error(`useLivlyUser must be used within a UserProvider`);
  }

  return context;
}
